// Renders a banner at the top of the screen - can be used for system notifications, emergency alerts, etc.
// See NotificationContext for implementation details

// Dependencies
import React, { useCallback } from "react";

// Material Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";

// Hooks
import { useNotifications } from "@/hooks/useNotifications";

interface Notification {
  type: string;
  message?: string;
  [key: string]: unknown;
}

const Notifications: React.FC = () => {
  const { notifications, clearNotifications } = useNotifications();

  const handleClose = useCallback(() => {
    clearNotifications();
  }, [clearNotifications]);

  const theme = useTheme();

  if (!notifications.length) return null;
  return (
    <>
      {notifications.map((n: Notification) => {
        const isNetwork = n.type === "Network";
        const bgColor = isNetwork
          ? theme.palette.error.main
          : theme.palette.primary.main;
        const textColor = isNetwork
          ? theme.palette.error.contrastText
          : theme.palette.primary.contrastText;
        return (
          <Box
            key={n.type}
            sx={{
              display: "flex",
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 1300,
              alignItems: "center",
              justifyContent: "space-between",
              color: textColor,
              backgroundColor: bgColor,
              padding: "16px 32px",
              minHeight: "56px",
              boxShadow: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: textColor }}>
                {n.message}
              </Typography>
            </Box>
            <IconButton
              aria-label="notification-close-button"
              sx={{
                color: textColor,
                "&:hover": {
                  backgroundColor: textColor,
                },
              }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        );
      })}
    </>
  );
};

export { Notifications };
